import {
  HStack,
  Button,
  Tooltip,
  IconButton,
  Popover,
  PopoverTrigger,
  Box,
  Text,
  useDisclosure,
  Image,
} from "@chakra-ui/react";
import { PersonOutlineOutlined, ThumbDownAlt } from "@mui/icons-material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BsThreeDots } from "react-icons/bs";
import { FaHourglassHalf } from "react-icons/fa";
import { FiAlertTriangle } from "react-icons/fi";
import { GiCardExchange, GiCrownedSkull, GiReturnArrow } from "react-icons/gi";
import { GrTransaction, GrUpload } from "react-icons/gr";
import { RiMenuAddFill } from "react-icons/ri";
import GaraazAlertDialogue from "../../../components/GaraazAlertDialogue";
import TextInputModal from "../../../components/TextInputModal";
import {
  OrderStatus,
  OrderActions,
  OrdersTextLabels,
  OrderPopoverLabels,
} from "../enums";
import GaraazChoser from "./GaraazChoser";
import GaraazDatePickerModalETA from "./GaraazDatePickerModalETA";
import GaraazGeneralPopover from "./GaraazGeneralPopover";

import UploadInvoiceModal, { invoiceInfoType } from "./UploadInvoiceModal";
import { useOrdersStore } from "../store/useOrdersStore";
import { useGetOrderMediaConfig } from "../../PlaceOrderTest/queryHooks";
import { useGetAllSuppliers, useGetSingleGaraazOrder } from "../queryHooks";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { AllocatedTo, startOrcaStates } from "./GaraazOrderComponent";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";

import services from "../services";
import useNotification, {
  useGaraazToastWithChildren,
} from "../../../utils/useNotification";
import OrderStatusChangeErrorBox from "./OrderStatusChangeErrorBox";
import { useUserDetailsContext } from "../../../context/userDetailsContext";
import GaraazCountDownClockV2 from "./GaraazCountDownClockV2";
import InvoicePopver from "./InvoicePopover";
import {
  dateFormatInvoiceApi,
  getCurrentUTCTimestamp,
} from "../../../utils/dateResolvers";
import Brightness1Icon from "@mui/icons-material/Brightness1";
import InvoicePopoverTrigger from "../../../components/InvoicePopoverTrigger";
import {
  GaraazInfoPopover,
  SearchListModal,
} from "@svan-garaaz/reactsharedlib";
import { redirectToOrderPage } from "../../../utils/redirectToOrderPage";
import GaraazInfoDialogue from "../../../components/GaraazInfoDialogue";
import updateOrderHandlerAllocationTime from "../../../utils/updateOrderHandlerAllocationTime";
import { META_INFO_KEYS } from "../../../utils/constants";
import updateAckTime from "../../../utils/updateAckTime";
import { OrderRelationType } from "../store/orderStrategies";
import { SupplierAssignmentHandler } from "../assignSupplierUtils";
import { useUnreadStore } from "../../Chat/store/useOrderStore";
import { useChatContext } from "../../../context/chatContext";
import { changeParticipantInConversation } from "../../../utils/conversationUtils";

type GaraazOrderComponentFooterReturnProps = {
  orderData: any;
  orderStatus: any;
  orderCancellationReason: string[];
  setOrderStatus: React.Dispatch<any>;
  handlePartConversionChangeScreen: () => void;
  activesupplierOrderStatus: AllocatedTo;
  refetchAllOrders: () => void;
  setEtaMissed: React.Dispatch<React.SetStateAction<boolean>>;
  isCustomerOrderFreezed: boolean;
  garaazRemarks: string;
  setGaraazRemarks: React.Dispatch<React.SetStateAction<string>>;
  supplierCancellationReasons: string;
  setSupplierCancellationReasons: React.Dispatch<React.SetStateAction<string>>;
  customerHoldReasons: string;
  setCustomerHoldReasons: React.Dispatch<React.SetStateAction<string>>;
  promiseTimeBreachReasons: string;
  setPromiseTimeBreachReasons: React.Dispatch<React.SetStateAction<string>>;
  isChildOrder: boolean;
  isChildOrdersVisible: boolean;
};

const GaraazOrderComponentFooterReturn: React.FC<
  GaraazOrderComponentFooterReturnProps
> = ({
  orderData,
  orderStatus,
  handlePartConversionChangeScreen,
  refetchAllOrders,
  setOrderStatus,
  setEtaMissed,
  activesupplierOrderStatus,
  isCustomerOrderFreezed,
  garaazRemarks,
  setGaraazRemarks,
  supplierCancellationReasons,
  setSupplierCancellationReasons,
  customerHoldReasons,
  setCustomerHoldReasons,
  promiseTimeBreachReasons,
  setPromiseTimeBreachReasons,
  isChildOrder,
  isChildOrdersVisible,
}) => {
  const toast = useNotification();
  const garaazToast = useGaraazToastWithChildren();
  const userDetails = useUserDetailsContext();
  const orderEta = useOrdersStore((state) => state.orderEtas[orderData._id]);
  const setSelectedOrderHandler = useOrdersStore(
    useCallback((store) => store.setSelectedOrderHandler, []),
  );
  const setOrderEtasWithOrderId = useOrdersStore(
    useCallback((state) => state.setOrderEtasWithOrderId, []),
  );
  const savedOrderRemarks = useOrdersStore(
    useCallback((state) => state.orderRemarks[orderData._id], [orderData._id]),
  );
  const setSavedOrderRemarks = useOrdersStore(
    useCallback((state) => state.setOrderRemarksWithOrderId, []),
  );
  const setOrderMetaInfoWithOrderId = useOrdersStore(
    useCallback((state) => state.setOrderMetaInfoWithOrderId, []),
  );

  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const { data: allSuppliers } = useGetAllSuppliers();
  const { data: parentOrder } = useGetSingleGaraazOrder(
    orderData.parentOrderId,
  );
  const hasEnquiryRoot =
    orderData?.rootOrderId && orderData.rootOrderId !== orderData._id;
  const { data: enquiryOrder } = useGetSingleGaraazOrder(
    orderData.rootOrderId ?? "",
    { enabled: !!hasEnquiryRoot },
  );

  const orderHandlers = useOrdersStore((state) => state.orderHandlers);
  const [invoiceInfo, setInvoiceInfo] = useState({
    title: "Garaaz",
    invoiceMediaId: orderData?.invoiceInfo?.invoiceMediaId ?? "",
    mrpInvoiceMediaId: orderData?.invoiceInfo?.mrpInvoiceMediaId ?? "",
    invoiceNo: orderData?.invoiceInfo?.invoiceNo ?? "",
    invoiceAmount: orderData?.invoiceInfo?.invoiceAmount ?? "",
    invoiceDate: orderData?.invoiceInfo?.invoiceDate ?? "",
    invoiceStatus: orderData?.invoiceInfo?.invoiceStatus ?? "",
    paymentLink: orderData?.invoiceInfo?.paymentLink ?? "",
  });
  const [challanInfo, setChallanInfo] = useState({
    title: "Garaaz",
    challanMediaId: orderData?.challanInfo?.challanMediaId ?? "",
    challanNo: orderData?.challanInfo?.challanNo ?? "",
    challanAmount: orderData?.challanInfo?.challanAmount ?? "",
    challanDate: orderData?.challanInfo?.challanDate ?? "",
  });
  const [supplierInvoiceInfo, setSupplierInvoiceInfo] = useState<
    {
      invoiceMediaId: any;
      invoiceNo: any;
      invoiceAmount: any;
      invoiceDate: any;
      paymentLink: any;
    }[]
  >(orderData.allocatedTo?.map((item: any) => item.invoiceInfo));
  const [supplierChallanInfo, setSupplierChallanInfo] = useState<
    {
      challanMediaId: any;
      challanNo: any;
      challanAmount: any;
      challanDate: any;
    }[]
  >(orderData.allocatedTo?.map((item: any) => item.challanInfo));
  const [ackClockValue0, setAckClockValue0] = useState(
    Math.floor(new Date(orderData.orderDate).getTime() / 1000),
  );

  const [ackClockValue100, setAckClockValue100] = useState(
    Math.floor(new Date(orderData.orderDate).getTime() / 1000) +
      mobileConfigs?.threshold_ack_order_mins * 60,
  );
  const [assignedSupplier, setAssignedSupplier] = useState<AllocatedTo[]>(
    orderData?.allocatedTo || [],
  );
  const [customerSupplierAssociations, setCustomerSupplierAssociations] =
    useState<{}[]>([]);
  const [cancellationReason, setCancellationReason] = useState(
    orderData?.cancellationReason ?? "",
  );
  const [ackTimeMissed, setAckTimeMissed] = useState(false);
  const [parentOrderActiveSupplier, setParentOrderActiveSupplier] =
    useState<any>(null);

  const orderHandlerOptions = orderHandlers.map((orderHandler) => {
    return {
      name: orderHandler.user.firstName + " " + orderHandler.user.lastName,
      menuItemIcon: (
        <Brightness1Icon
          sx={{
            color: orderHandler?.isActive ? "green" : "red",
            fontSize: "10px",
          }}
        />
      ),
      ...orderHandler,
    };
  });
  const orderHandlerName =
    (orderData?.orderHandler[0]?.firstName ?? "-") +
    " " +
    (orderData?.orderHandler[0]?.lastName ?? "");
  const transactionTypeOption = [{ type: "Facilitation" }, { type: "Trading" }];

  const autoAllocateSupplierName =
  (allSuppliers ?? []).find(
    (s: any) =>
      String(s?._id ?? "").trim() ===
      String(parentOrderActiveSupplier ?? "").trim(),
  )?.businessName ?? "";

  const {
    isOpen: etaPickerModalOpen,
    onOpen: openETAPickerModal,
    onClose: closeETAPickerModal,
  } = useDisclosure();

  const {
    isOpen: updateInvoiceModalOpen,
    onOpen: openUpdateInvoiceModal,
    onClose: closeUpdateInvoiceModal,
  } = useDisclosure();

  const {
    isOpen: orderRemarksModelOpen,
    onOpen: openOrdersRemarksModel,
    onClose: closeOrdersRemarksModel,
  } = useDisclosure();

  const {
    isOpen: isOpenACCEPT_RETURNOrderDialogue,
    onOpen: onOpenACCEPT_RETURNOrderDialogue,
    onClose: onCloseACCEPT_RETURNOrderDialogue,
  } = useDisclosure();

  const {
    isOpen: isOpenREJECT_RETURNOrderDialogue,
    onOpen: onOpenREJECT_RETURNOrderDialogue,
    onClose: onCloseREJECT_RETURNOrderDialogue,
  } = useDisclosure();

  const {
    isOpen: isSupplierModalListOpen,
    onOpen: onSupplierModalListOpen,
    onClose: onSupplierModalListClose,
  } = useDisclosure();

  const {
    isOpen: isOrderDetailsExtractedByPhantomModalOpen,
    onOpen: onOrderDetailsExtractedByPhantomModalOpen,
    onClose: onOrderDetailsExtractedByPhantomModalClose,
  } = useDisclosure();

  useEffect(() => {
    let customerSupplierAssociationsTemp: {}[] = [];

    orderData?.customer?.supplierAssociations?.map(
      (association: any, index: number) => {
        let customerSupplierAssociationObject = {
          "Supplier Name": association?.supplier?.name
            ? association?.supplier?.businessName
            : "-",
          "Branch Name": association?.supplier?.branches
            ? association?.supplier?.branches[0]?.branchName
            : "-",

          "Branch Code": association?.supplier?.branches
            ? association?.supplier?.branches[0]?.branchCode
            : "-",
          "Supplier Code": association?.supplierCode
            ? association.supplierCode
            : "-",
        };
        customerSupplierAssociationsTemp.push(
          customerSupplierAssociationObject,
        );
      },
    );
    setCustomerSupplierAssociations(customerSupplierAssociationsTemp);
    setInvoiceInfo({
      title: "Garaaz",
      invoiceMediaId: orderData?.invoiceInfo?.invoiceMediaId,
      mrpInvoiceMediaId: orderData?.invoiceInfo?.mrpInvoiceMediaId,
      invoiceNo: orderData?.invoiceInfo?.invoiceNo,
      invoiceDate: orderData?.invoiceInfo?.invoiceDate,
      invoiceAmount: orderData?.invoiceInfo?.invoiceAmount,
      invoiceStatus: orderData?.invoiceInfo?.invoiceStatus,
      paymentLink: orderData?.invoiceInfo?.paymentLink,
    });
    setChallanInfo({
      title: "Garaaz",
      challanMediaId: orderData?.challanInfo?.challanMediaId,
      challanNo: orderData?.challanInfo?.challanNo,
      challanDate: orderData?.challanInfo?.challanDate,
      challanAmount: orderData?.challanInfo?.challanAmount,
    });
    setSupplierInvoiceInfo(
      orderData.allocatedTo.map((item: any) => ({
        ...item.invoiceInfo,
        supplierId: item.supplierId,
      })),
    );
    setSupplierChallanInfo(
      orderData.allocatedTo.map((item: any) => ({
        ...item.challanInfo,
        supplierId: item.supplierId,
      })),
    );
    setAssignedSupplier(orderData?.allocatedTo);
    if (orderData.orderType === "RETURN") {
      (async () => {
        const parentOrder = await services.getSingleOrder(
          orderData.parentOrderId,
        );
        const activeSupplier = parentOrder?.allocatedTo?.find(
          (item: any) => item.isActive === true,
        );

        setParentOrderActiveSupplier(activeSupplier?.supplierId);
      })();
    }

    setAckClockValue0(
      Math.floor(new Date(orderData.orderDate).getTime() / 1000),
    );
    setAckClockValue100(
      Math.floor(new Date(orderData.orderDate).getTime() / 1000) +
        mobileConfigs?.threshold_ack_order_mins * 60,
    );
    setAckTimeMissed(false);
  }, [orderData]);

  const saveOrderRemarks = async (
    remarks: string,
    garaazOrderRemarks?: string,
    supplierCancellationRemarksValue?: string,
    customerHoldRemarksValue?: string,
    promiseTimeBreachRemarksValue?: string,
  ) => {
    try {
      if (remarks) {
        let newOrderRemarks = [
          ...savedOrderRemarks,
          {
            userName: `${userDetails.firstName} ${userDetails.lastName}`,
            remarks: remarks,
          },
        ];
        setSavedOrderRemarks(orderData._id, newOrderRemarks);
        await services.setOrderRemarks(orderData._id, {
          userName: `${userDetails.firstName} ${userDetails.lastName}`,
          remarks,
        });
      }
      if (garaazOrderRemarks !== orderData?.garaazRemarks) {
        try {
          setGaraazRemarks(garaazOrderRemarks || "");
          const response = await services.updateOrder(orderData._id, {
            garaazRemarks: garaazOrderRemarks,
          });
        } catch (error) {
          toast({
            description: "Some error occured in Updating garaaz remarks",
            status: "error",
          });
        }
      }
      if (
        supplierCancellationRemarksValue !==
        orderData?.supplierCancellationReasons
      ) {
        try {
          setSupplierCancellationReasons(
            supplierCancellationRemarksValue || "",
          );
          const response = await services.updateOrder(orderData._id, {
            supplierCancellationReasons: supplierCancellationRemarksValue,
          });
        } catch (error) {
          toast({
            description:
              "Some error occured in Updating supplier cancellation reasons",
            status: "error",
          });
        }
      }
      if (customerHoldRemarksValue !== orderData?.customerHoldReasons) {
        try {
          setCustomerHoldReasons(customerHoldRemarksValue || "");
          const response = await services.updateOrder(orderData._id, {
            customerHoldReasons: customerHoldRemarksValue,
          });
        } catch (error) {
          toast({
            description: "Some error occured in Updating customer hold reasons",
            status: "error",
          });
        }
      }
      if (
        promiseTimeBreachRemarksValue !== orderData?.promiseTimeBreachReasons
      ) {
        try {
          setPromiseTimeBreachReasons(promiseTimeBreachRemarksValue || "");
          const response = await services.updateOrder(orderData._id, {
            promiseTimeBreachReasons: promiseTimeBreachRemarksValue,
          });
        } catch (error) {
          toast({
            description:
              "Some error occured in Updating promise time breach reasons",
            status: "error",
          });
        }
      }
    } catch (error) {}
  };

  const handleorderETAModalSubmit = async (
    etaGivenTime: string,
    eta: string,
    reason: string,
  ) => {
    try {
      const newEtaChangeReason = [...orderEta, { etaGivenTime, eta, reason }];
      setOrderEtasWithOrderId(orderData._id, newEtaChangeReason);
      await services.setOrderETA(orderData._id, {
        etaGivenTime,
        eta,
        reason,
      });
      setEtaMissed(false);
    } catch (error) {}
  };

  const handleOrderHandlerChange = async (item: any) => {
    try {
      await services.updateOrder(orderData._id, {
        orderHandler: item.userId,
      });
      await updateOrderHandlerAllocationTime(
        orderData._id,
        setOrderMetaInfoWithOrderId,
      );
      try {
        const currentOrderHandler = orderData.orderHandler?.[0];
        const activeSupplier = orderData.allocatedTo?.find(
          (item: any) => item.isActive === true,
        );

        if (
          !activeSupplier?.supplierId ||
          !orderData?.orderNo ||
          !currentOrderHandler?._id
        ) {
          console.warn("Participant change skipped: missing prerequisites", {
            hasActiveSupplier: !!activeSupplier?.supplierId,
            hasOrderNo: !!orderData?.orderNo,
            hasCurrentHandler: !!currentOrderHandler?._id,
          });
        } else {
          const uniqueNameForSupplierConv = `order:${orderData.orderNo}|supplier:${activeSupplier.supplierId}`;
          const uniqueNameForCustomerConv = `order:${orderData.orderNo}|customer:${orderData.customerId}`;
          await changeParticipantInConversation({
            conversationName: uniqueNameForSupplierConv,
            oldHandlerUserId: currentOrderHandler._id,
            newHandlerUserId: item.userId,
          });

          await changeParticipantInConversation({
            conversationName: uniqueNameForCustomerConv,
            oldHandlerUserId: currentOrderHandler._id,
            newHandlerUserId: item.userId,
          });
        }
      } catch (error) {
        console.warn("Participant change failed:", error);
      }

      setSelectedOrderHandler(
        orderData._id,
        item.user,
        isChildOrder ? OrderRelationType.DESCENDANT : OrderRelationType.ROOT,
        orderData.rootOrderId,
      );
    } catch (error) {
      console.error("Failed to assign order handler:", error);
    }
  };

  const handleAssignSupplierChange = async (item: any) => {
    const result = await SupplierAssignmentHandler.handleFullFulfillment(
      orderData._id,
      item._id,
      setOrderMetaInfoWithOrderId,
      item.businessLegalName,
    );

    !!refetchAllOrders && refetchAllOrders();
    if (result.success) {
      toast({
        description: result.message,
        status: "success",
      });
    } else {
      let errorFromResponse;
      try {
        errorFromResponse = JSON.parse(result.message);
      } catch (e) {
        errorFromResponse = [];
      }

      const title = !!errorFromResponse?.length
        ? errorFromResponse[0]?.title
        : OrdersTextLabels.ACCESS_DENIED_ORDER_STATUS_INVALID;

      garaazToast({
        title: title,
        status: "error",
        render: <OrderStatusChangeErrorBox errorMsg={errorFromResponse} />,
      });
    }
  };

  const handleAcceptReturnAndAllocate = async () => {
    try{
    await services.checkNextStateOfGaraazOrders(
      orderData._id,
      OrderActions.CUSTOMER_RETURNED,
    );
  
    if (parentActiveSupplierId) {
      await services.allocateSupplierOrder(orderData._id, {
        supplierId: parentActiveSupplierId,
      });
    }
  
    setOrderStatus(OrderStatus.CUSTOMER_RETURNED);
    onCloseACCEPT_RETURNOrderDialogue();
    !!refetchAllOrders && refetchAllOrders();
  }catch(error:any){
    const errorFromResponse = error?.response?.data?.errorMsg ?? [];
    const title =
      errorFromResponse.length && errorFromResponse[0]?.title
        ? errorFromResponse[0].title
        : OrdersTextLabels.ACCESS_DENIED_ORDER_STATUS_INVALID;

    garaazToast({
      title,
      status: "error",
      render: <OrderStatusChangeErrorBox errorMsg={errorFromResponse} />,
    });
  }
  };

  const handleChangeOrderStatus = async (
    status: OrderStatus | OrderActions,
    modalCloseFunction?: () => void,
  ) => {
    try {
      if (status === OrderStatus.ACK) {
        await updateAckTime(orderData._id, setOrderMetaInfoWithOrderId);
      }
      await services.checkNextStateOfGaraazOrders(orderData._id, status);
      setOrderStatus(status);

      !!refetchAllOrders && refetchAllOrders();
    } catch (error: any) {
      const errorFromResponse = error?.response?.data?.errorMsg;
      const title = !!errorFromResponse.length
        ? errorFromResponse[0]?.title
        : OrdersTextLabels.ACCESS_DENIED_ORDER_STATUS_INVALID;
      garaazToast({
        title: title,
        status: "error",
        render: <OrderStatusChangeErrorBox errorMsg={errorFromResponse} />,
      });
    } finally {
      if (modalCloseFunction) modalCloseFunction();
    }
  };

  const handleUpdateInvoiceModalSubmit = async (
    pdfFile: File | null = null,
    invoiceInfo: invoiceInfoType,
    challanInfo: any,
    fileType: string,
  ) => {
    try {
      let updatedParts = {};
      if (fileType === "Invoice") {
        if (
          pdfFile !== null &&
          invoiceInfo.invoiceNo &&
          invoiceInfo.invoiceAmount
        ) {
          const formData = new FormData();
          formData.append("media", pdfFile);
          const mediaResult = await services.addMedia(formData);

          updatedParts = {
            invoiceInfo: {
              ...invoiceInfo,
              invoiceMediaId: mediaResult[0]._id,
              invoiceDate: dateFormatInvoiceApi(invoiceInfo.invoiceDate),
            },
          };
        } else {
          updatedParts = {
            invoiceInfo: {
              ...invoiceInfo,
              invoiceDate: dateFormatInvoiceApi(invoiceInfo.invoiceDate),
              invoiceMediaId:
                invoiceInfo.invoiceNo &&
                invoiceInfo.invoiceAmount &&
                invoiceInfo.invoiceDate
                  ? orderData?.invoiceInfo?.invoiceMediaId
                  : undefined,
            },
          };
        }

        const updatedOrderResponse = await services.updateOrder(
          orderData._id,
          updatedParts,
        );

        setInvoiceInfo({
          title: "Garaaz",
          invoiceMediaId: updatedOrderResponse.invoiceInfo.invoiceMediaId,
          mrpInvoiceMediaId: updatedOrderResponse.invoiceInfo.mrpInvoiceMediaId,
          invoiceNo: updatedOrderResponse.invoiceInfo.invoiceNo,
          invoiceAmount: updatedOrderResponse.invoiceInfo.invoiceAmount,
          invoiceDate: updatedOrderResponse.invoiceInfo.invoiceDate,
          invoiceStatus: updatedOrderResponse.invoiceInfo.invoiceStatus,
          paymentLink: updatedOrderResponse.invoiceInfo.paymentLink,
        });

        toast({
          description: OrdersTextLabels.INVOICE_UPLOADED_SUCCESSFULLY,
          status: "success",
        });
      } else {
        if (
          pdfFile !== null &&
          challanInfo.challanNo &&
          challanInfo.challanAmount
        ) {
          const formData = new FormData();
          formData.append("media", pdfFile);
          const mediaResult = await services.addMedia(formData);

          updatedParts = {
            challanInfo: {
              ...challanInfo,
              challanMediaId: mediaResult[0]._id,
              challanDate: dateFormatInvoiceApi(challanInfo.challanDate),
            },
          };
        } else {
          updatedParts = {
            challanInfo: {
              ...challanInfo,
              challanDate: dateFormatInvoiceApi(challanInfo.challanDate),
              challanMediaId:
                challanInfo.challanNo &&
                challanInfo.challanAmount &&
                challanInfo.challanDate
                  ? orderData?.challanInfo?.challanMediaId
                  : undefined,
            },
          };
        }

        const updatedOrderResponse = await services.updateOrder(
          orderData._id,
          updatedParts,
        );

        setChallanInfo({
          title: "Garaaz",
          challanMediaId: updatedOrderResponse.challanInfo.challanMediaId,
          challanNo: updatedOrderResponse.challanInfo.challanNo,
          challanAmount: updatedOrderResponse.challanInfo.challanAmount,
          challanDate: updatedOrderResponse.challanInfo.challanDate,
        });

        toast({
          description: OrdersTextLabels.CHALLAN_UPLOADED_SUCCESSFULLY,
          status: "success",
        });
      }
    } catch (error) {
      toast({
        description: OrdersTextLabels.SOMETHING_WENT_WRONG,
        status: "error",
      });
    }
  };

  const uniqueNumbersInQuickEtas = useMemo(() => {
    return mobileConfigs?.quick_etas_mins
      ?.split(",")
      ?.filter(
        (value: any, index: number, array: any) =>
          array.indexOf(value) === index && !isNaN(parseInt(value)),
      );
  }, [mobileConfigs]);

  async function onAckTimeComplete() {
    const response = await services.throwACKMissedEvent(orderData._id);
  }
  const garaazSupplierId = String(mobileConfigs?.garaaz_supplier_id ?? "").trim();

  const parentActiveSupplierId = String(parentOrderActiveSupplier ?? "").trim();

  return (
    <HStack
      flex={1}
      height={"12%"}
      borderRadius={"0px 0px 8px 8px"}
      borderTop={"1px solid lightgrey"}
      borderBottom={"1px solid lightgrey"}
      style={{
        margin: 0,
      }}
      display={"flex"}
      padding={`${startOrcaStates.includes(orderStatus) ? 0 : "0.8rem"}`}
      py={`${startOrcaStates.includes(orderStatus) ? 0 : "1.5rem"}`}
      alignItems={"center"}
      bgColor={"white"}
    >
      {startOrcaStates.includes(orderStatus) ? (
        <>
          <Button
            borderTopLeftRadius={0}
            borderTopRightRadius={0}
            height={"100%"}
            width={"100%"}
            bg={"green.500"}
            _hover={{ bg: "green.600" }}
            color={"white"}
            onClick={() => handleChangeOrderStatus(OrderStatus.ACK)}
          >
            <Text fontSize={"lg"}>ACK</Text>
            {!orderEta.length && (
              <Box ml="5" display={"flex"}>
                {!ackTimeMissed && (
                  <GaraazCountDownClockV2
                    componentSize="small"
                    value0={ackClockValue0}
                    value100={ackClockValue100}
                    onCompleteTime={onAckTimeComplete}
                    isCompleted={ackTimeMissed}
                    setIsCompleted={setAckTimeMissed}
                  ></GaraazCountDownClockV2>
                )}

                {ackTimeMissed && (
                  <HStack spacing={1}>
                    <FiAlertTriangle
                      color="red"
                      style={{ marginLeft: "2px" }}
                    />
                    <Text>ACK Missed</Text>
                  </HStack>
                )}
              </Box>
            )}
          </Button>
        </>
      ) : (
        <>
          <HStack spacing={3} w="45%">
            <Box>
              <Tooltip label="Flip View">
                <IconButton
                  icon={<GiCardExchange />}
                  backgroundColor={"#BBE6E3"}
                  color={"black"}
                  aria-label={""}
                  onClick={handlePartConversionChangeScreen}
                />
              </Tooltip>
            </Box>
            <Box>
              <GaraazChoser
                ButtonIcon={<PersonOutlineOutlined />}
                ButtonToolTipLabel="Assign Order Handler"
                MenuItemOnChange={handleOrderHandlerChange}
                displayKey="name"
                options={orderHandlerOptions}
                MenuButtonProps={{ isDisabled: isCustomerOrderFreezed }}
                showIcon={true}
              />
            </Box>
            <Box>
              <Tooltip label="Accept Return">
                <IconButton
                  icon={<ThumbUpAltIcon />}
                  backgroundColor={"#BBE6E3"}
                  color={"black"}
                  aria-label={""}
                  onClick={onOpenACCEPT_RETURNOrderDialogue}
                  _hover={{ backgroundColor: "green.400" }}
                  isDisabled={isCustomerOrderFreezed || orderStatus !== OrderStatus.ACK}
                />
              </Tooltip>
              <GaraazAlertDialogue
                alertBodyText={
                  `Upon accepting, this return order will be assigned to ` +
                  `${autoAllocateSupplierName} supplier. This cannot be undone. Are you sure?`
                }
                alertDialogueHeaderText="Accept Return Order"
                onSubmitBtn={handleAcceptReturnAndAllocate}
                isOpen={isOpenACCEPT_RETURNOrderDialogue}
                onClose={onCloseACCEPT_RETURNOrderDialogue}
                onOpen={onOpenACCEPT_RETURNOrderDialogue}
                submitBtnText="Confirm"
              />
            </Box>
            <Box>
              <Tooltip label="Reject Return">
                <IconButton
                  icon={<ThumbDownAlt />}
                  backgroundColor={"#BBE6E3"}
                  color={"black"}
                  aria-label={""}
                  onClick={onOpenREJECT_RETURNOrderDialogue}
                  _hover={{ backgroundColor: "red.400" }}
                  isDisabled={isCustomerOrderFreezed || orderStatus !== OrderStatus.ACK}
                />
              </Tooltip>
              <GaraazAlertDialogue
                alertBodyText="Are you sure? You can't undo this action afterwards."
                alertDialogueHeaderText="Reject Return Order"
                onSubmitBtn={() =>
                  handleChangeOrderStatus(
                    OrderActions.CUSTOMER_RETURN_REJECTED,
                    onCloseREJECT_RETURNOrderDialogue,
                  )
                }
                isOpen={isOpenREJECT_RETURNOrderDialogue}
                onClose={onCloseREJECT_RETURNOrderDialogue}
                onOpen={onOpenREJECT_RETURNOrderDialogue}
                submitBtnText="Confirm"
              />
            </Box>

            <Box>
              <Tooltip label="Assign Supplier">
                <IconButton
                  icon={<GiCrownedSkull />}
                  backgroundColor={"#BBE6E3"}
                  color={"black"}
                  aria-label={""}
                  onClick={() => {
                    if (
                      orderData?.isOrderItemsExtractedByOpenAI ||
                      orderData?.isVehicleInfoExtractedByOpenAI
                    ) {
                      onOrderDetailsExtractedByPhantomModalOpen();
                    } else {
                      onSupplierModalListOpen();
                    }
                  }}
                  isDisabled={isCustomerOrderFreezed}
                />
              </Tooltip>
              <SearchListModal
                options={(allSuppliers ?? [])?.filter((item: any) => {
                  const supplierId = String(item?._id ?? "").trim();
                  return(
                    supplierId === parentActiveSupplierId ||
                    supplierId === garaazSupplierId
                  );
                })}
                displayKey="businessName"
                handleSelectListItem={handleAssignSupplierChange}
                modalHeading="Assign Supplier"
                searchInputPlaceholder="Search Supplier By Name"
                isOpen={isSupplierModalListOpen}
                onClose={onSupplierModalListClose}
                zeroOptionsMessage={
                  <>
                    <Text>
                      No Supplier Found that caters to the specified brands.
                    </Text>
                    <Text>Split the Order or find new supplier.</Text>
                  </>
                }
              ></SearchListModal>
              <GaraazInfoDialogue
                isOpen={isOrderDetailsExtractedByPhantomModalOpen}
                onOpen={onOrderDetailsExtractedByPhantomModalOpen}
                onClose={() => {
                  onOrderDetailsExtractedByPhantomModalClose();
                  onSupplierModalListOpen();
                }}
                leftBtnText={"Already Reviewed"}
                submitBtnText={"Review"}
                onSubmitBtn={onOrderDetailsExtractedByPhantomModalClose}
                alertBodyText={
                  "This order might contain some order items or vehicle related information extracted by Phantom. Please review before proceeding."
                }
                alertGenericHeaderText={
                  <Text fontSize={"xl"} display={"flex"} alignItems={"center"}>
                    Phantom Alert !
                    <Image
                      width={30}
                      src="https://garaaz-store.s3.ap-south-1.amazonaws.com/7a6e6ff21a3b4239bf82d94d38a7e5ef.png"
                    ></Image>
                  </Text>
                }
              ></GaraazInfoDialogue>
            </Box>
            <Box>
              <Tooltip label="ETA">
                <IconButton
                  icon={<FaHourglassHalf />}
                  backgroundColor={"#BBE6E3"}
                  color={"black"}
                  aria-label={""}
                  onClick={openETAPickerModal}
                  isDisabled={isCustomerOrderFreezed}
                />
              </Tooltip>
              <GaraazDatePickerModalETA
                modelHeader="Please provide ETA for Customer"
                isOpen={etaPickerModalOpen}
                orderETAs={orderEta}
                handleSubmit={handleorderETAModalSubmit}
                onClose={closeETAPickerModal}
                modifiedOrderETAs={uniqueNumbersInQuickEtas}
              />
            </Box>
            <Box>
              <Tooltip label="Upload Invoice">
                <IconButton
                  isDisabled={
                    activesupplierOrderStatus?.orderStatus !==
                      OrderStatus.ACCEPTED || isCustomerOrderFreezed
                  }
                  icon={<GrUpload />}
                  backgroundColor={"#BBE6E3"}
                  color={"black"}
                  aria-label={""}
                  onClick={openUpdateInvoiceModal}
                />
              </Tooltip>
              <UploadInvoiceModal
                isOpen={updateInvoiceModalOpen}
                orderInvoiceInfo={invoiceInfo}
                handleSubmit={handleUpdateInvoiceModalSubmit}
                onClose={closeUpdateInvoiceModal}
                orderChallanInfo={challanInfo}
              />
            </Box>
            <Box>
              <Tooltip label="Add Order Remarks">
                <IconButton
                  icon={<RiMenuAddFill />}
                  backgroundColor={"#BBE6E3"}
                  color={"black"}
                  aria-label={""}
                  onClick={openOrdersRemarksModel}
                />
              </Tooltip>
              <TextInputModal
                modalHeader="Order Remarks"
                inputPlaceHolder="Add Order Remarks"
                isOpen={orderRemarksModelOpen}
                onSave={saveOrderRemarks}
                onClose={closeOrdersRemarksModel}
                displaySecondaryInput={true}
                secondaryInput={garaazRemarks || ""}
                modalSecondaryHeader="Garaaz Remarks"
                secondaryInputPlaceHolder="Add Garaaz Remarks"
                displayThirdInput={true}
                modalThirdHeader="Supplier Cancellation Reasons"
                thirdInputPlaceHolder="Add Supplier Cancellation Reason"
                thirdInput={supplierCancellationReasons || ""}
                displayFourthInput={true}
                modalFourthHeader="Customer Hold Reasons"
                fourthInputPlaceHolder="Add Customer Hold Reason"
                fourthInput={customerHoldReasons || ""}
                displayFifthInput={true}
                modalFifthHeader="Promise Time Breach Reasons"
                fifthInputPlaceHolder="Add Promise Time Breach Reason"
                fifthInput={promiseTimeBreachReasons || ""}
              />
            </Box>
          </HStack>
          <Box
            display={"flex"}
            style={{ marginLeft: "auto", alignItems: "start" }}
            paddingTop={5}
          >
            <GaraazInfoPopover
              triggerLabel={`P-RETURN`}
              triggerStyle={{
                fontSize: "13px",
                color: "#1A202C",
                marginRight: "19px",
              }}
              groups={[
                {
                  subgroups: [
                    {
                      title: "Parent Return Order Number",
                      items: [
                        {
                          value: parentOrder?.orderNo,
                          key: "orderNo",
                          copyable: true,
                          onClick: () => {
                            redirectToOrderPage(parentOrder?.orderNo);
                          },
                          itemStyle: {
                            cursor: "pointer",
                            marginLeft: "-1px",
                          },
                        },
                      ],
                      subgroupTitleStyle: {
                        marginLeft: "-10px",
                        fontSize: "20px",
                      },
                    },
                    ...(hasEnquiryRoot && enquiryOrder?.orderNo
                      ? [
                          {
                            title: OrderPopoverLabels.ENQUIRY_NO,
                            items: [
                              {
                                value: enquiryOrder.orderNo,
                                key: "orderNo",
                                copyable: true,
                                onClick: () => {
                                  redirectToOrderPage(enquiryOrder.orderNo);
                                },
                                itemStyle: {
                                  cursor: "pointer",
                                  marginLeft: "-1px",
                                  fontSize: "14px",
                                },
                              },
                            ],
                            subgroupTitleStyle: {
                              marginLeft: "-10px",
                              fontSize: "20px",
                            },
                          },
                        ]
                      : []),
                  ],
                },
              ]}
            />
            <GaraazGeneralPopover
              ArekeysVisible={true}
              CopierKeys={["Supplier Code"]}
              title="Associations"
              value={customerSupplierAssociations}
              placement={"top-start"}
            >
              <PopoverTrigger>
                <Text
                  fontWeight={"bold"}
                  color={"#1A202C"}
                  cursor={"pointer"}
                  display={"flex"}
                >
                  ASSOCIATIONS
                </Text>
              </PopoverTrigger>
            </GaraazGeneralPopover>
            {(invoiceInfo?.invoiceNo ||
              supplierInvoiceInfo.some((item: any) => item?.invoiceNo)) && (
              <InvoicePopver
                copierKeys={["invoiceNo", "challanNo"]}
                Invoices={[invoiceInfo, ...supplierInvoiceInfo]}
                Challans={[challanInfo, ...supplierChallanInfo]}
              >
                <InvoicePopoverTrigger
                  invoiceStatus={invoiceInfo.invoiceStatus}
                ></InvoicePopoverTrigger>
              </InvoicePopver>
            )}
            <Box ml={5}>
              <Text fontWeight={"bold"} color={"#1A202C"}>
                HANDLER
              </Text>
              <Text
                color="teal.600"
                textTransform={"uppercase"}
                fontWeight={"semibold"}
                fontSize={"x-small"}
              >
                {`${orderHandlerName}`}
              </Text>
            </Box>
          </Box>
        </>
      )}
    </HStack>
  );
};

export default GaraazOrderComponentFooterReturn;
